import {Geocoder} from '../core/geocoder/models/geocoder';
import {GeocoderReq} from '../core/geocoder/models/geocoder-req';
import * as moment from 'moment-timezone';
import {Unit} from '../units/models/unit';
import { SettingsModel } from '../core/models/settings.model';
import { DataHelper } from './helpers/data.helper';
import { Routes } from './models/routes';
import { VehicleAddressSize } from '../settings/models/vehicle-address-size';
import { VehicleAddressSizeEnum } from '../settings/models/vehicle-address-size.enum';

export class Utils {

  private dateTimeFormat: string;

  constructor() {
    this.dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';
  }

  utcDateString(date) {
    var formatedDate = null;
    if (date) {
      const month = (date.getUTCMonth() + 1) > 9 ? (date.getUTCMonth() + 1) : '0' + (date.getUTCMonth() + 1);
      const day = date.getUTCDate() > 9 ? date.getUTCDate() : '0' + date.getUTCDate();
      const hour = date.getUTCHours() > 9 ? date.getUTCHours() : '0' + date.getUTCHours();
      const minute = date.getUTCMinutes() > 9 ? date.getUTCMinutes() : '0' + date.getUTCMinutes();
      const seconds = date.getUTCSeconds() > 9 ? date.getUTCSeconds() : '0' + date.getUTCSeconds();
      formatedDate = date.getUTCFullYear() + '-' + month + '-' + day + 'T' + hour + ':' + minute + ':' + seconds + 'Z';
    }
    return formatedDate;
  }

  getUnitTag = (unit: Unit, tag: string) => {
    if (!unit) { return ''; }
    let selectedTag = unit.unitTags.filter((item) => item.tag == tag)[0];
    return selectedTag ? selectedTag.value : '';
  };

  debounce(func, wait, immediate?) {
    let timeout;
    return function() {
      let context = this, args = arguments;
      let later = function() {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      let callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  };

  shortGeocoderAddress(geocoder: Geocoder) {
    let address = '';
    if (geocoder) {

      if (geocoder.city || geocoder.town || geocoder.village) {
        address += geocoder.city || geocoder.town || geocoder.village;
      }

      if (geocoder.road) {
        address += ' ' + geocoder.road;
      }

      if (geocoder.houseNumber) {
        address += ' ' + geocoder.houseNumber;
      }
      //
      // if ((geocoder.road || geocoder.houseNumber) && (geocoder.city || geocoder.town || geocoder.village || geocoder.village)) {
      //   address += ', ';
      // }

    }
    return address;
  }

  private _withPostalGeocoderAddress(geocoder: Geocoder) {
    let address = this.shortGeocoderAddress(geocoder); 
    address += `, ${geocoder.postcode}`;
    return address;
  }

  setAddress(geocoder: Geocoder) {
    let address = {
      short: '',
      withPostal: '',
      full: ''
    };
    if (geocoder) {
      address.short = this.shortGeocoderAddress(geocoder);
      address.withPostal = this._withPostalGeocoderAddress(geocoder);
      address.full = geocoder.address;
    }
    return address;
  }

  partDataGeocoder(geocoderData: GeocoderReq[]) {
    const amountData = 50;
    let partData = [];

    if(geocoderData.length > amountData) {
      const iteration = geocoderData.length / amountData;

      for (let i = 0; i <= iteration; i++) {
        const data = geocoderData.slice((i * amountData), ((i + 1) * amountData));
        partData.push(data);
      }
    } else {
      partData[0] = geocoderData;
    }

    return partData;
  }

  dateToUtc(date) {
    if (!date) {
      return;
    }
    const utcDate = moment.utc(date);
    return utcDate.format(this.dateTimeFormat);
  }

  dateToLocal(date) {
    if (!date) {
      return;
    }
    const localDate = moment(moment.utc(date)).local();
    return localDate.format(this.dateTimeFormat);
  }

  formatDateUTC(dateString) {
    return moment.utc(moment(dateString, 'YYYY-MM-DD HH:mm:ss' ).utc().format('YYYY-MM-DD HH:mm:ssZ')).toISOString();
  }

  arrayUnique(array) {
    let a = array.concat();
    for (let i  = 0; i < a.length; ++i) {
      for (let j = i + 1; j < a.length; ++j) {
        if (a[i] === a[j]) {
          a.splice(j--, 1);
        }
      }
    }

    return a;
  }

  sort<T>(a: T | undefined, b: T | undefined, direction: "asc" | "desc") {
    const multiplier = direction === 'asc' ? 1 : -1;
    if (
      (typeof a === 'number' || a === undefined) &&
      (typeof b === 'number' || b === undefined)
    ) {
      const numA = Number(a ?? 0);
      const numB = Number(b ?? 0);
      return (numA - numB) * multiplier;
    }

    if (a === undefined && b !== undefined) return 1 * multiplier;
    if (a !== undefined && b === undefined) return -1 * multiplier;
    if (a === undefined && b === undefined) return 0;

    if (typeof a === 'boolean' && typeof b === 'boolean') {
      return (Number(a) - Number(b)) * multiplier;
    }

    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b) * multiplier;
    }

    return String(a).localeCompare(String(b)) * multiplier;
  }

  hasValue = (value: any): boolean => {
    return DataHelper.hasValue(value);
  }

  isNotEmpty = (value: any): boolean => {
    return this.hasValue(value) && value !== '';
  }

  moveArrayItem = (array, fromIndex, toIndex) => {
    const arr = [...array];
    arr.splice(toIndex, 0, ...arr.splice(fromIndex, 1));
    return arr;
  }

  containsValue = (item: any, value: any) => {
    if (item instanceof Object) {
      return Object.values(item)?.some((val: any) => this.containsValue(val, value));
    } else if (item instanceof String) {
      return item?.toLowerCase()?.indexOf(value?.toString()?.trim()?.toLowerCase()) > -1;
    } else {
      return this.hasValue(item) ? item?.toString()?.toLowerCase()?.indexOf(value?.toString()?.trim()?.toLowerCase()) > -1 : false;
    }
  }

  compare = (a: number | string, b: number | string, isAsc: boolean) => {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  addToArrayIfNotExist = (array: any[], item: any) => {
    if (array && item && !array.some(x => x === item)) {
      array.push(item);
    }
    return array;
  }

  formatDate = (dateObject) => {
    let dateString = null;
    if (dateObject) {
      const month = dateObject.month > 9 ? dateObject.month : '0' + dateObject.month;
      const day = dateObject.day > 9 ? dateObject.day : '0' + dateObject.day;
      dateString = dateObject.year + '-' + month + '-' + day;
      dateString +=  'T' + '00:00:00';
    }
    return this.formatDateUTC(dateString);
  }


  trackByFn(index, item) {
    return item && item.id ? item.id : index;
  }

  uuid() {
    let uuidValue = "";
    let k;
    let randomValue;
    for (k = 0; k < 32;k++) {
      randomValue = Math.random() * 16 | 0;

      if (k == 8 || k == 12 || k == 16 || k == 20) {
        uuidValue += "-"
      }
      uuidValue += (k == 12 ? 4 : (k == 16 ? (randomValue & 3 | 8) : randomValue)).toString(16);
    }
    return uuidValue;
  }

  convertKmToMeters = (valueInKm) => {
    return valueInKm ? Math.round(valueInKm * 1000) : 0;
  }
  convertMetersToKm = (valueInMeters) => {
    return valueInMeters ? valueInMeters / 1000 : 0;
  }

  settingsContainKey(settings: SettingsModel[], key: string) {
    if (!settings || !key) { return false; }
    return settings.some((x: SettingsModel) => x.key.toUpperCase() === key.toUpperCase());
  }

  getSettingsByKey(settings: SettingsModel[], key: string): SettingsModel {
    if (!settings || !key) { return null; }
    return settings.find((x: SettingsModel) => x.key.toUpperCase() === key.toUpperCase());
  }

  prepareDataToGeocoderRequest(route: any, lang, alwaysGetEndAddress?: boolean) {
    if (!route) { return; }
    let geocoderRequest = [];
    if (route.startPosition) {
      geocoderRequest.push({
        id: 1,
        latitude: route.startPosition ? route.startPosition.latitude : null,
        longitude: route.startPosition ? route.startPosition.longitude : null,
        languageCode: lang
      });
    } else {
      route.startAddress = 'n/d';
    }
    if ((route.startPosition && route.stopPosition && (route.startPosition.latitude !== route.stopPosition.latitude || route.startPosition.longitude !== route.stopPosition.longitude)) || alwaysGetEndAddress) {
      geocoderRequest.push({
        id: 2,
        latitude: route.stopPosition ? route.stopPosition.latitude : null,
        longitude: route.stopPosition ? route.stopPosition.longitude : null,
        languageCode: lang
      });
    } else {
      route.endAddress = 'n/d';
    }
    return [geocoderRequest];
  }

  addAddressToRoutes(route: any, geocoderData: Geocoder[],
    vehicleAddressSize: VehicleAddressSize = 'short') {
    if (!route.startAddress || !route.endAddress) {
      const start = geocoderData.find(geocoder => route.startPosition && geocoder.id === 1);
      const end = geocoderData.find(geocoder => route.stopPosition && geocoder.id === 2);
      let size: string;

      switch(vehicleAddressSize) {
        case VehicleAddressSizeEnum.Short: {
          size = 'short'
          break;
        }
        case VehicleAddressSizeEnum.WithPostal: {
          size = 'withPostal'
          break;
        }
        case VehicleAddressSizeEnum.Full: {
          size = 'full'
          break;
        }
      }

      if (start) {
        route.startAddress = this.setAddress(start)[size];
      }

      if (end) {
        route.endAddress = this.setAddress(end)[size];
      }      
    }
  }
}
